import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";
import { sortGrades } from "../utils/SchoolAdmin/translations";

/**
 * Custom hook to manage school/grade/subject selection for differentiation
 */
const useDifferentiationSelection = ({
    currentTimePeriodDto,
    gradingProfilesCurrentSem,
    schoolLevelTemplateDto,
    schoolYearDto,
    token,
    userDetails,
    dispatch
}) => {
    const [allSharedGroupOptions, setAllSharedGroupOptions] = useState([]);
    const [selectedGrade, setSelectedGrade] = useState(null);
    const [selectedSchoolLocationKey, setSelectedSchoolLocationKey] = useState(null);
    const [sharedGradingProfileDtos, setSharedGradingProfileDtos] = useState([]);
    const fetchedSharedGroupsRef = useRef(false);

    const sharedLocationKeys = useMemo(() => {
        // Only include locations that have at least one group with EDIT permission
        const locationsWithEditPermission = new Set();
        (allSharedGroupOptions || []).forEach((g) => {
            const groupShareDto = g.groupShareDto || g.sharedMeta?.groupShareDto;
            const hasEditPermission = groupShareDto?.gradingCriteriaShares?.some(
                (share) => share.permission === "EDIT"
            );
            if (hasEditPermission) {
                locationsWithEditPermission.add(String(g.sharedLocationKey));
            }
        });

        const shared = Array.from(locationsWithEditPermission).filter(Boolean);
        const homeroomSchool = String(schoolLevelTemplateDto?.firstSemester?.profileLocationKey);

        // Only add homeroom school if there are shared groups from OTHER schools
        if (homeroomSchool && gradingProfilesCurrentSem?.length > 0) {
            const hasOtherSchools = shared.some((key) => String(key) !== String(homeroomSchool));
            if (hasOtherSchools && !shared.includes(String(homeroomSchool))) {
                return [String(homeroomSchool), ...shared];
            }
        }
        return shared;
    }, [allSharedGroupOptions, gradingProfilesCurrentSem, schoolLevelTemplateDto]);

    const homeroomGrades = useMemo(() => {
        return [...new Set((gradingProfilesCurrentSem || []).map((g) => g.gradeName).filter(Boolean))];
    }, [gradingProfilesCurrentSem]);

    // Helper function to check if teacher has EDIT permission for any group in a grade
    const hasEditPermissionForGrade = useCallback(
        (gradeName) => {
            if (!gradeName) return false;
            // Check if there's at least one group for this grade with EDIT permission on any criteria
            return (allSharedGroupOptions || []).some((g) => {
                if (g.sharedGradeName !== gradeName) return false;
                // Check if this group has EDIT permission for any grading criteria
                // Permission info can be in groupShareDto directly or in sharedMeta.groupShareDto
                const groupShareDto = g.groupShareDto || g.sharedMeta?.groupShareDto;
                return groupShareDto?.gradingCriteriaShares?.some((share) => share.permission === "EDIT");
            });
        },
        [allSharedGroupOptions]
    );

    const sharedGrades = useMemo(() => {
        const allSharedGrades = [
            ...new Set((allSharedGroupOptions || []).map((g) => g.sharedGradeName).filter(Boolean))
        ];
        // Filter to only include grades where teacher has EDIT permission on at least one group
        return allSharedGrades.filter((grade) => hasEditPermissionForGrade(grade));
    }, [allSharedGroupOptions, hasEditPermissionForGrade]);

    const combinedGrades = useMemo(() => {
        return [...new Set([...homeroomGrades, ...sharedGrades])];
    }, [homeroomGrades, sharedGrades]);

    const gradesForSelect = useMemo(() => {
        // If a school location is selected, only show grades for that location
        if (selectedSchoolLocationKey) {
            const homeroomSchool = schoolLevelTemplateDto?.firstSemester?.profileLocationKey;
            const isHomeroomSchool = Number(homeroomSchool) === Number(selectedSchoolLocationKey);

            // Get grades from homeroom if this is the homeroom school
            const gradesForLocation = isHomeroomSchool ? homeroomGrades : [];

            // Add shared grades for this location (filtered to only EDIT permission grades)
            const sharedGradesForLocation = sharedGrades.filter((grade) => {
                // Check if this grade has any groups in the selected location
                return (allSharedGroupOptions || []).some(
                    (g) =>
                        g.sharedGradeName === grade && Number(g.sharedLocationKey) === Number(selectedSchoolLocationKey)
                );
            });

            return sortGrades([...new Set([...gradesForLocation, ...sharedGradesForLocation])]);
        }

        // Show all grades where teacher has homeroom OR shared access with EDIT permission
        // Use sharedGrades which is already filtered to only include EDIT permission grades
        return sortGrades([...new Set([...homeroomGrades, ...sharedGrades])]);
    }, [selectedSchoolLocationKey, homeroomGrades, sharedGrades, allSharedGroupOptions, schoolLevelTemplateDto]);

    const fetchAllSharedGroups = useCallback(() => {
        if (!userDetails || !token || !schoolYearDto?.key || !currentTimePeriodDto?.key) return;

        const options = {
            action: "getAllSharedGroups",
            params: {
                teacherGuid: userDetails.uid,
                schoolYearKey: schoolYearDto.key,
                timePeriod: currentTimePeriodDto.key
            },
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const sharedGroups = response.data.payload;
                    const manipulatedGroups = sharedGroups.map((group) => ({
                        value: `shared-${group.sharedGroupId}`,
                        label: group.sharedGroupName,
                        sharedGradingProfile: group.sharedGradingProfile,
                        groupType: "SHARED",
                        sharedGradeName: group.sharedGradeName,
                        sharedLocationKey: group.locationKey,
                        sharedMeta: group
                    }));
                    setAllSharedGroupOptions(manipulatedGroups);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, [schoolYearDto?.key, currentTimePeriodDto?.key, token, userDetails]);

    useEffect(() => {
        if (fetchedSharedGroupsRef.current) return;
        fetchedSharedGroupsRef.current = true;
        fetchAllSharedGroups();
    }, [fetchAllSharedGroups]);

    useEffect(() => {
        // Only auto-select if there's exactly one school option
        if (sharedLocationKeys.length === 1 && !selectedSchoolLocationKey) {
            setSelectedSchoolLocationKey(sharedLocationKeys[0]);
        }
    }, [sharedLocationKeys, selectedSchoolLocationKey]);

    useEffect(() => {
        if (!selectedGrade && gradesForSelect.length > 0) {
            const nextGrade = gradesForSelect[0];
            setSelectedGrade(nextGrade);
            const defaultEpr = gradingProfilesCurrentSem?.find((p) => p.gradeName === nextGrade) || null;
            dispatch({ type: "SET_SELECTED_EPR", payload: defaultEpr });
        }
    }, [gradesForSelect, selectedGrade, gradingProfilesCurrentSem, dispatch]);

    // Fetch shared grading profile DTOs for the selected grade
    useEffect(() => {
        if (!selectedGrade || !allSharedGroupOptions?.length || !token) {
            setSharedGradingProfileDtos([]);
            return;
        }

        const profileIds = [
            ...new Set(
                allSharedGroupOptions
                    .filter((g) => g.sharedGradeName === selectedGrade)
                    .map((g) => g.sharedGradingProfile)
                    .filter(Boolean)
            )
        ];

        if (!profileIds.length) {
            setSharedGradingProfileDtos([]);
            return;
        }

        let alive = true;
        Promise.all(
            profileIds.map((id) =>
                ElementaryProgressReportDao({ action: "getGradingProfileById", gradingProfileId: id, token })
            )
        )
            .then((responses) => {
                if (!alive) return;
                setSharedGradingProfileDtos(responses.map((r) => r?.data?.payload).filter(Boolean));
            })
            .catch((error) => {
                console.error("Error fetching shared grading profiles:", error);
            });

        return () => {
            alive = false;
        };
    }, [selectedGrade, allSharedGroupOptions, token]);

    return {
        selectedGrade,
        setSelectedGrade,
        selectedSchoolLocationKey,
        setSelectedSchoolLocationKey,
        sharedLocationKeys,
        gradesForSelect,
        combinedGrades,
        allSharedGroupOptions,
        sharedGradingProfileDtos
    };
};

export default useDifferentiationSelection;
